import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { getImageAtSize } from '../../lib/utils';

import { useSemaineSite } from '../../contexts/SiteContext';
import Button from '../buttons/Button';

import DocWeek from '../../../img/doc-week-logo.png';
import styles from '../../../styles/partials/image.module.css';

const propTypes = {
    image: PropTypes.shape({}),
    credits: PropTypes.string,
    size: PropTypes.string,
    aspectRatio: PropTypes.string,
    alt: PropTypes.string,
    format: PropTypes.string,
    contain: PropTypes.bool,
    filtered: PropTypes.bool,
    filteredHover: PropTypes.bool,
    rounded: PropTypes.bool,
    header: PropTypes.bool,
    square: PropTypes.bool,
    loading: PropTypes.string,
    onClick: PropTypes.func,
    withCredits: PropTypes.bool,
    withDocWeek: PropTypes.bool,
    withDocLink: PropTypes.bool,
    className: PropTypes.string,
    imageClassName: PropTypes.string,
    imageButtonClassName: PropTypes.string,
};

const Image = ({
    image = null,
    credits = null,
    size = 'large',
    aspectRatio = null,
    alt = null,
    format = null,
    contain = false,
    filtered = false,
    filteredHover = false,
    rounded = false,
    header = false,
    square = false,
    loading = 'lazy',
    onClick = null,
    withCredits = false,
    withDocWeek = false,
    withDocLink = false,
    className = null,
    imageClassName = null,
    imageButtonClassName = null,
}) => {
    const { domain = null } = useSemaineSite();
    const imageSize = getImageAtSize(image, size);
    const { url = null, width = null, height = null } = imageSize || {};
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (url !== null) {
            const img = document.createElement('img');
            img.src = url;
            img.onload = () => {
                setIsLoading(false);
            };
        }
    }, [url]);

    const docElement = useMemo(() => {
        if (!withDocWeek) {
            return null;
        }
        return withDocLink ? (
            <a
                href={`https://${domain}`}
                target="blank"
                rel="noopener noreferrer"
                className={styles.docWeek}
            >
                <img className={styles.docImage} src={DocWeek} alt="DocWeek" />
            </a>
        ) : (
            <div className={styles.docWeek}>
                <img className={styles.docImage} src={DocWeek} alt="DocWeek" />
            </div>
        );
    }, [withDocLink, domain, withDocWeek]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles[format]]: format !== null,
                    [styles.contain]: contain,
                    [styles.isLoading]: isLoading,
                    [styles.withCredits]: withCredits,
                    [styles.filtered]: filtered,
                    [styles.filteredHover]: filteredHover,
                    [styles.rounded]: rounded,
                    [styles.header]: header,
                    [styles.square]: square,
                    [className]: className !== null,
                },
            ])}
        >
            {onClick !== null ? (
                <Button
                    onClick={(e) => onClick(e)}
                    className={classNames([
                        styles.imageButton,
                        { [imageButtonClassName]: imageButtonClassName !== null },
                    ])}
                >
                    <img
                        className={classNames([
                            styles.image,
                            { [imageClassName]: imageClassName !== null },
                        ])}
                        src={url}
                        alt={alt}
                        loading={loading}
                        draggable="false"
                        width={width}
                        height={height}
                        style={{ aspectRatio }}
                    />
                </Button>
            ) : (
                <img
                    className={classNames([
                        styles.image,
                        { [imageClassName]: imageClassName !== null },
                    ])}
                    src={url}
                    alt={alt}
                    loading={loading}
                    draggable="false"
                    width={width}
                    height={height}
                    style={{ aspectRatio }}
                />
            )}
            {filtered ? <div className={styles.filter} /> : null}
            {withCredits && credits !== null ? (
                <div className={styles.credits}>
                    <FormattedMessage defaultMessage="Credits :" description="Credit label" />{' '}
                    {credits}
                </div>
            ) : null}
            {withDocWeek ? docElement : null}
        </div>
    );
};

Image.propTypes = propTypes;

export default Image;
